import { TooltipDirective } from '../../../../../auxiliares/tooltip/tooltip.directive';
import { BOTONES } from '../../../../../componentes/botones';
import { Component, ViewEncapsulation, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { CardHeaderComponent } from '../../../../../componentes/card-header/card-header.component';
import { ContentCardComponent } from '../../../../../componentes/content-card/content-card.component';
import { FormularioDialogService } from '../../../../../auxiliares/servicios/formulario-dialog.service';
import { IConfigReenvio, IReglaReenvio } from 'modelos/src';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import {
  EmergenciaAvlDialogComponent,
  IEmergenciaAvlData,
} from '../../config-reenvios/emergencia-avl-dialog/emergencia-avl-dialog.component';

export interface IReenviosTrackerData {
  /** Nombre / identificación de la entidad, para el subtítulo */
  nombreEntidad?: string;
  configs: IConfigReenvio[];
  /** idTracker: habilita ajustar/detener la emergencia AVL. Sin esto el dialog
   *  queda de solo lectura, como estaba. */
  idTracker?: string;
}

/**
 * Una config de reenvío está vigente si está activa y la fecha actual cae
 * dentro de su período (periodoInicio / periodoFin son opcionales).
 */
export function configReenvioVigente(config: IConfigReenvio): boolean {
  if (config.activo === false) return false;
  const ahora = Date.now();
  if (
    config.periodoInicio &&
    new Date(config.periodoInicio).getTime() > ahora
  ) {
    return false;
  }
  if (config.periodoFin && new Date(config.periodoFin).getTime() < ahora) {
    return false;
  }
  return true;
}

interface IReenvioFila {
  config: IConfigReenvio;
  ajustable: boolean;
  metodo: string;
  icono: string;
  vigente: boolean;
  agrupacion?: string;
  entidad?: string;
  iconoEntidad?: string;
  clienteReenvio?: string;
  destino?: string;
  protocolo?: string;
  fechaCreacion?: string;
  periodo?: string;
  reenviarHijos: boolean;
  reglas: IReglaReenvio[];
}

@Component({
  selector: 'app-reenvios-tracker-dialog',
  templateUrl: './reenvios-tracker-dialog.component.html',
  styleUrl: './reenvios-tracker-dialog.component.scss',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [
    TooltipDirective,
    MatCardModule,
    BOTONES,
    MatIconModule,

    CardHeaderComponent,
    ContentCardComponent,
  ],
})
export class ReenviosTrackerDialogComponent {
  public data = inject<IReenviosTrackerData>(MAT_DIALOG_DATA);
  public dialogRef =
    inject<MatDialogRef<ReenviosTrackerDialogComponent>>(MatDialogRef);
  private formularios = inject(FormularioDialogService);
  private helper = inject(HelperService);

  /** Se cambió algo: el que abrió el dialog debería refrescar su listado. */
  private huboCambios = false;

  public puedeEditar = this.helper.tienePermiso(
    'Administración - Editar configuraciones de reenvíos',
  );

  public filas: IReenvioFila[] = this.armarFilas();

  /** Abre el popup de emergencia AVL sobre una config existente: extender el
   *  período o detenerla en el momento. */
  public ajustar(fila: IReenvioFila): void {
    const idTracker = this.data?.idTracker;
    if (!idTracker || !fila.config?._id) return;
    this.formularios
      .abrir<EmergenciaAvlDialogComponent, IEmergenciaAvlData>(
        EmergenciaAvlDialogComponent,
        {
          tamano: 'mediano',
          data: {
            idTracker,
            config: fila.config,
            nombreEntidad: this.data?.nombreEntidad,
          },
        },
      )
      .afterClosed()
      .subscribe((actualizada?: IConfigReenvio) => {
        if (!actualizada) return;
        this.huboCambios = true;
        // Reemplazo local para que la tarjeta refleje el cambio sin recargar.
        const i = this.data.configs.findIndex((c) => c._id === actualizada._id);
        if (i >= 0) this.data.configs[i] = actualizada;
        this.filas = this.armarFilas();
      });
  }

  public cerrar(): void {
    this.dialogRef.close(this.huboCambios || undefined);
  }

  private armarFilas(): IReenvioFila[] {
    return (this.data?.configs ?? []).map((config) => {
      const opts = config.opcionesReenvio;
      // Logictracker siempre reenvía por http, no usa el protocolo configurado
      const protocolo =
        opts?.metodo === 'Logictracker' ? 'http' : opts?.protocolo;
      return {
        config,
        // Solo emergencias AVL por entidad: el popup es específico de AVL, y
        // una config de "todos los trackers del cliente" afecta a muchos
        // vehículos — detenerla desde acá sería un efecto colateral silencioso.
        ajustable:
          !!config._id &&
          opts?.metodo === 'Objetivo AVL' &&
          config.agrupacionReenvio === 'Entidad' &&
          !!this.data?.idTracker &&
          this.puedeEditar,
        metodo: opts?.metodo ?? 'Sin método',
        icono: this.getIconoMetodo(opts?.metodo),
        vigente: configReenvioVigente(config),
        agrupacion: config.agrupacionReenvio,
        entidad: config.tracker?.nombre ?? config.dispositivoAlarma?.nombre,
        iconoEntidad: config.tracker?.nombre
          ? 'gps_fixed'
          : config.dispositivoAlarma?.nombre
            ? 'doorbell'
            : undefined,
        clienteReenvio: config.clienteReenvio?.nombre,
        destino:
          opts?.host && opts?.puerto
            ? `${opts.host}:${opts.puerto}`
            : opts?.host,
        protocolo,
        fechaCreacion: config.fechaCreacion
          ? HelperService.getFechaYHora(config.fechaCreacion)
          : undefined,
        periodo: this.getPeriodo(config),
        reenviarHijos: !!config.reenviarHijos,
        reglas: opts?.usaReglaReenvio ? (opts.reglasReenvio ?? []) : [],
      };
    });
  }

  private getPeriodo(config: IConfigReenvio): string | undefined {
    const inicio = config.periodoInicio
      ? HelperService.getFecha(config.periodoInicio)
      : null;
    const fin = config.periodoFin
      ? HelperService.getFecha(config.periodoFin)
      : null;
    if (!inicio && !fin) return undefined;
    return `${inicio ?? 'Sin inicio'} → ${fin ?? 'Sin fin'}`;
  }

  private getIconoMetodo(metodo?: string): string {
    switch (metodo) {
      case 'Básico':
        return 'send';
      case 'Seguridad Evento Externo':
        return 'security';
      case 'Soflex':
        return 'cable';
      case 'Caessat':
      case 'Cersat':
        return 'cell_tower';
      case 'Iron Tracking':
        return 'route';
      case 'Logictracker':
        return 'track_changes';
      default:
        return 'settings_input_antenna';
    }
  }
}
